// M3 offshore logistics (M3_SPEC §4, §5): contracted supply boats, crew boats, helicopters, anchor handlers, standby
// vessels at a day rate; deck-storage resupply, weekly crew changes (boat within 60 mi, else a helicopter with the
// range), helicopter crash risk, back-to-back offshore crews. state.logistics[uid] (lazy), state.m3.supply / crewChange.
import { chance, range } from '../../core/rng.js';
import { eqDef, fieldDef, stat, isAvailable, yf } from '../defs.js';
import { LOGISTICS, OFFSHORE } from '../../data/m3/rules.js';
import { m3, logisticsState, serviceRate, envelope, rigBuiltYear, distanceToShore, crewBoatOk, month, envOf } from './opsutil.js';
import { m3Accident, m3Mitigation } from './opsincidents.js';
import { activeRigCrew, crewWage } from '../crew.js';
import { spend, nextId, fmt$ } from '../util.js';
import { hasUpgrade } from '../research.js';

const ok = (msg, extra = {}) => ({ ok: true, msg, ...extra });
const no = (msg) => ({ ok: false, msg });
const svc = (def) => stat(def, 'service', null);

export function contractServiceImpl(state, defId, targetUid) {
  const def = eqDef(defId);
  if (!def || def.cat !== 'logistics') return no('Not a logistics service.');
  if (!isAvailable(def, state)) return no(`${def.name} is not available until ${def.year}.`);
  if (stat(def, 'perJob', false)) return no(`${def.name} is hired per job (subsea installs and interventions) automatically.`);
  const rig = state.rigs[targetUid];
  const plat = state.platforms?.[targetUid];
  if (!rig && !plat) return no('Choose an offshore rig or platform to serve.');
  if (rig && !eqDef(rig.defId)?.stats?.offshore) return no(`${rig.name} is a land rig.`);
  const L = logisticsState(state);
  if (Object.values(L).some((l) => l.defId === defId && l.assignedTo?.id === targetUid)) return no(`${def.name} already serves it.`);
  const uid = nextId(state, 'lg');
  L[uid] = { uid, defId, assignedTo: { kind: rig ? 'rig' : 'platform', id: targetUid }, startDay: state.day };
  return ok(`${def.name} contracted for ${rig ? rig.name : eqDef(plat.defId)?.name} at ${fmt$(serviceRate(state, def))}/day.`, { uid });
}

export function releaseServiceImpl(state, uid) {
  const l = state.logistics?.[uid];
  if (!l) return no('No such contract.');
  delete state.logistics[uid];
  return ok(`${eqDef(l.defId)?.name} released.`);
}

export const IDLE_SERVICE_MULT = 0.25; // a boat or helicopter on call for an idle rig bills a stand-down rate
function serviceWorking(state, l) {
  const id = l.assignedTo?.id;
  if (!id) return false;
  const r = state.rigs[id];
  if (r) return !!(r.wellId || r.charterOut);
  return !!state.platforms?.[id];
}
export const servicesFor = (state, id) => Object.values(state.logistics ?? {}).filter((l) => l.assignedTo?.id === id);

// Best helicopter on contract for a unit that can reach the field (range × reserve covers the distance).
export function heliFor(state, id, fieldId) {
  const d = distanceToShore(fieldId);
  return servicesFor(state, id).map((l) => eqDef(l.defId)).filter((def) => svc(def) === 'air' && stat(def, 'rangeMi', 0) * LOGISTICS.helicopterRangeReserve >= d)[0] ?? null;
}
export function heliReaches(def, fieldId) {
  return stat(def, 'rangeMi', 0) * LOGISTICS.helicopterRangeReserve >= distanceToShore(fieldId);
}

// Daily: charge day rates, resupply, crew changes, helicopter risk, offshore wage premium (back-to-back crews).
export function tickLogistics(state) {
  const L = state.logistics;
  let cost = 0;
  if (L) for (const l of Object.values(L)) cost += serviceRate(state, eqDef(l.defId)) * (serviceWorking(state, l) ? 1 : IDLE_SERVICE_MULT);
  if (cost > 0) spend(state, cost, 'logistics', false);
  const M = m3(state);
  let extraWages = 0;
  for (const rig of Object.values(state.rigs)) {
    const def = eqDef(rig.defId);
    if (!def?.stats?.offshore) continue;
    const w = rig.wellId ? state.wells[rig.wellId] : null;
    if (!w?.offshore) continue;
    const e = envelope(def, rigBuiltYear(rig));
    const hs = state.fields[w.fieldId]?.sea?.hsFt ?? 0;
    const mine = servicesFor(state, rig.uid).map((l) => eqDef(l.defId));
    // resupply (deck storage)
    const boat = mine.find((d) => svc(d) === 'supply');
    const crewBoat = mine.find((d) => svc(d) === 'crew');
    M.supply[rig.uid] ??= e.deckStorageDays;
    if (boat && hs <= stat(boat, 'weatherLimitHsFt', 15)) M.supply[rig.uid] = e.deckStorageDays;
    else if (crewBoat && hs <= stat(crewBoat, 'weatherLimitHsFt', 8)) M.supply[rig.uid] = Math.min(e.deckStorageDays, M.supply[rig.uid] + 0.5);
    // crew change every crewChangeEveryDays; fatigue after the hitch if nobody can get out there
    const last = M.crewChange[rig.uid] ?? state.day;
    M.crewChange[rig.uid] = last;
    const heli = mine.find((d) => svc(d) === 'air' && heliReaches(d, w.fieldId) && hs <= stat(d, 'weatherLimitHsFt', 30));
    const byBoat = crewBoat && crewBoatOk(w.fieldId) && hs <= stat(crewBoat, 'weatherLimitHsFt', 8);
    const crew = activeRigCrew(state, rig);
    if (state.day - last >= OFFSHORE.crewChangeEveryDays && (heli || byBoat)) {
      M.crewChange[rig.uid] = state.day;
      if (heli && !byBoat) heliFlight(state, rig, w, heli, crew);
    } else if (state.day - last > OFFSHORE.hitchDays) {
      for (const c of crew) c.fatigue = Math.min(100, c.fatigue + 3);
    }
    if (crew.length) extraWages += crew.reduce((a, c) => a + crewWage(state, c), 0) * (1.5 * OFFSHORE.backToBackCrews - 1);
  }
  if (extraWages > 0) spend(state, extraWages, 'wages', false);
}

// One crew-change flight (weekly): crashRiskPerYear is per rig served, so the per-flight risk is × 7/365.
export function heliCrashChance(state, heliDef, fieldId) {
  const env = envOf(fieldId);
  const mon = month(state);
  const foggy = env?.fog?.months?.includes(mon) || env?.winterStorms?.months?.includes(mon);
  return stat(heliDef, 'crashRiskPerYear', 0.002) * OFFSHORE.crewChangeEveryDays / 365 * (foggy ? 2 : 1);
}
export function heliFlight(state, rig, well, heliDef, crew) {
  const p = heliCrashChance(state, heliDef, well.fieldId);
  state.company.stats.heliFlights = (state.company.stats.heliFlights ?? 0) + 1;
  if (!chance(state, p)) return false;
  state.company.stats.helicopterCrashes = (state.company.stats.helicopterCrashes ?? 0) + 1;
  let deaths = 0;
  if (chance(state, LOGISTICS.helicopterFatalShare * (hasUpgrade(state, 'survival_training') ? 0.7 : 1) * (1 - m3Mitigation(state, rig, 'helicopter_crash')))) {
    const seats = Math.min(crew.length, stat(heliDef, 'pob', 3));
    deaths = Math.round(seats * range(state, LOGISTICS.helicopterCrashDeaths[0], LOGISTICS.helicopterCrashDeaths[1]));
  }
  m3Accident(state, 'helicopter_crash', { rig, well, crew, deaths, text: `The ${heliDef.name} went down on the crew change to ${fieldDef(well.fieldId)?.name}.`, extra: { press: deaths > 0 ? 'platformDisaster' : null } });
  return true;
}

export function logisticsView(state, rigUid) {
  const rig = state.rigs[rigUid];
  const plat = state.platforms?.[rigUid];
  if (!rig && !plat) return null;
  const fieldId = rig ? (rig.wellId ? state.wells[rig.wellId]?.fieldId : rig.location?.fieldId ?? rig.at?.fieldId) : plat.fieldId;
  const def = eqDef(rig ? rig.defId : plat.defId);
  const e = rig ? envelope(def, rigBuiltYear(rig)) : null;
  const contracted = servicesFor(state, rigUid).map((l) => ({ uid: l.uid, defId: l.defId, name: eqDef(l.defId)?.name, service: svc(eqDef(l.defId)), dayRate: serviceRate(state, eqDef(l.defId)) }));
  const has = (s) => contracted.some((c) => c.service === s);
  const dist = fieldId ? distanceToShore(fieldId) : null;
  const heliOk = contracted.some((c) => c.service === 'air' && fieldId && heliReaches(eqDef(c.defId), fieldId));
  const required = [];
  if (rig && e?.offshore) {
    required.push({ service: 'supply', label: 'Supply boat', ok: has('supply') || has('crew') });
    required.push({ service: dist != null && dist > LOGISTICS.crewBoatMaxMi ? 'air' : 'crew', label: dist != null && dist > LOGISTICS.crewBoatMaxMi ? `Helicopter (≥ ${Math.ceil(dist / LOGISTICS.helicopterRangeReserve)} mi range)` : 'Crew boat or helicopter', ok: dist != null && dist > LOGISTICS.crewBoatMaxMi ? heliOk : has('crew') || heliOk });
    if (e.stationKeeping === 'moored') required.push({ service: 'anchor', label: 'Anchor handler (for moves)', ok: has('anchor') });
    if (fieldDef(fieldId)?.regionId === 'north_sea_uk' && yf(state) >= 1976) required.push({ service: 'standby', label: 'Standby vessel (UK, 1976+)', ok: has('standby'), optional: true });
  }
  return { id: rigUid, fieldId, distanceToShoreMi: dist, contracted, required, supplyDaysLeft: rig ? (state.m3?.supply?.[rigUid] ?? e?.deckStorageDays ?? null) : null,
    deckStorageDays: e?.deckStorageDays ?? null, lastCrewChange: state.m3?.crewChange?.[rigUid] ?? null,
    dailyCost: contracted.reduce((a, c) => a + c.dayRate, 0) };
}
